import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import useApiCall from '../../../../../../../hooks/useApiCall';
import VehiclesService from '../../../../../../../services/affreightment/VehiclesService';
import useGetGeneralAppData from '../../../../../../../hooks/useGetGeneralAppData';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { VehicleFromApiType } from '../../types';
import { MultiValue } from 'react-select';

interface UseLoadFormInterface {
  setIsSelectLoading: Dispatch<SetStateAction<boolean>>;
  setIsLinesSelectLoading: Dispatch<SetStateAction<boolean>>;
  setAffreightmentLines: Dispatch<SetStateAction<MultiValue<{ value: string, label: string }>>>;
  setWorkplace: Dispatch<SetStateAction<{ value: number, label: string }>>;
  setModel: Dispatch<SetStateAction<string | number>>;
  setTransportType: Dispatch<SetStateAction<{ value: number, label: string }>>;
  setTransportTypeValue: Dispatch<SetStateAction<number | string>>;
  setVehicleCode: Dispatch<SetStateAction<string>>;
  setBrand: Dispatch<SetStateAction<string>>;
  setCapacity: Dispatch<SetStateAction<string | number>>;
  setConsumption: Dispatch<SetStateAction<string | number>>;
  setIsLinesSelectDisabled: Dispatch<SetStateAction<boolean>>;
  getLinesOfWorkplace: (workplaceCode: number | string) => Promise<void>;
  edit: boolean;
}

export default function useLoadForm({
  setIsSelectLoading,
  setAffreightmentLines,
  setWorkplace,
  setModel,
  setTransportType,
  setTransportTypeValue,
  setVehicleCode,
  setBrand,
  setCapacity,
  setConsumption,
  setIsLinesSelectDisabled,
  getLinesOfWorkplace,
  edit,
}: UseLoadFormInterface) {
  const [isLoadLoading, setIsLoadLoading] = useState(false);
  const [apiVehicleFetched, setApiVehicleFetched] = useState(true);
  const [apiWorkplaceFetched, setApiWorkplaceFetched] = useState(true);
  const [doesListWorkplacesExists, setDoesListWorkplacesExists] = useState(true);
  const [workplacesOptions, setWorkplacesOptions] = useState<{ value: number, label: string }[]>([]);

  const { apiCall } = useApiCall();
  const {
    userCode,
    userProfile,
    companyCode,
  } = useGetGeneralAppData();
  const { vehicleId } = useParams();

  const transportKindOptions = [
    { value: 1, label: 'Ônibus' },
    { value: 2, label: 'Van' },
    { value: 3, label: 'Micro' },
    { value: 4, label: 'Outros' },
  ];

  const transportKindLiterals = {
    1: 'Ônibus',
    2: 'Van',
    3: 'Micro',
    4: 'Outros',
  };

  const getVehicle = useCallback(async () => {
    let workplaceOfVehicle: string | number = '';
    await apiCall({
      apiToCall: VehiclesService.getVehicle,
      queryParams: {
        codUsu: userCode,
        codVeiculoSis: vehicleId,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
      },
      onStartLoad: () => setIsLoadLoading(true),
      onEndLoad: () => setIsLoadLoading(false),
      actionAfterResponse: (response: VehicleFromApiType) => {
        if (!response.codigo) {
          toast.error(`Não foi possível recuperar os dados do veículo. Por favor, tente novamente (${response.msg})`);
          setApiVehicleFetched(false);
          return;
        }
        const vehicleData = response.veiculo;
        const linesAssociated = response.linhasFretaAssoc;
        if (response.linhasAssociadas) {
          setAffreightmentLines(linesAssociated.map((line) => (
            { value: line.routeID, label: line.letreiro }
          )));
        }
        if (response.locTrabLinhas.codLocTrab) {
          setWorkplace({
            value: response.locTrabLinhas.codLocTrab,
            label: response.locTrabLinhas.descrLocTrab,
          });
          workplaceOfVehicle = response.locTrabLinhas.codLocTrab;
        }
        setModel(String(vehicleData.modelo));
        setTransportType({ value: vehicleData.CodtipoTransp, label: vehicleData.tipoTransporte });
        setTransportTypeValue(vehicleData.CodtipoTransp);
        setVehicleCode(vehicleData.codVeiculo);
        setBrand(vehicleData.marca);
        setCapacity(vehicleData.capacidade);
        setConsumption(vehicleData.consumo);
        setApiVehicleFetched(true);
      },
      catchAction: () => setApiVehicleFetched(false),
      catchMessage: 'Não foi possível recuperar os dados do veículo',
    });

    if (workplaceOfVehicle) {
      await getLinesOfWorkplace(workplaceOfVehicle);
    }
  }, [apiCall, userCode, vehicleId, userProfile, companyCode, setModel, setTransportType, setTransportTypeValue, setVehicleCode, setBrand, setCapacity, setConsumption, setAffreightmentLines, setWorkplace, getLinesOfWorkplace]);

  const getWorkplaces = useCallback(async () => {
    await apiCall({
      apiToCall: VehiclesService.getWorkplaces,
      queryParams: { codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
      onStartLoad: () => setIsSelectLoading(true),
      onEndLoad: () => setIsSelectLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível recuperar os locais de trabalho (${response.msg})`);
          setApiWorkplaceFetched(false);
          return;
        }
        const workplacesData: { codLocTrab: number, descrLocTrab: string }[] = response.locaisTrabalho;
        const workplaceOptions = workplacesData?.map((lt) => (
          { value: lt.codLocTrab, label: lt.descrLocTrab }
        ));
        setWorkplacesOptions(workplaceOptions);
        setDoesListWorkplacesExists(!!response.listaLT);
        setIsLinesSelectDisabled(!response.listaLT);
        setApiWorkplaceFetched(true);
      },
      catchAction: () => setApiWorkplaceFetched(false),
      catchMessage: 'Não foi possível recuperar os locais de trabalho',
    });
  }, [apiCall, companyCode, setIsLinesSelectDisabled, setIsSelectLoading, userCode, userProfile]);

  useEffect(() => {
    if (edit) {
      getVehicle();
    }
    getWorkplaces();
  }, [edit, getWorkplaces, getVehicle]);

  function handleGetVehicleTryAgain() {
    getVehicle();
  }

  function handleGetWorkplacesTryAgain() {
    getWorkplaces();
  }

  return {
    apiVehicleFetched,
    apiWorkplaceFetched,
    doesListWorkplacesExists,
    workplacesOptions,
    handleGetVehicleTryAgain,
    handleGetWorkplacesTryAgain,
    isLoadLoading,
    transportKindOptions,
    transportKindLiterals,
  };
}
